import { useEffect, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";

import { GetNationality } from "@/services/nationality.service";
import { normalizeNationalityValue, type NationalityOption } from "@/lib/nationality";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

type Props = {
  value: string;
  onSelect: (value: string) => void;
  error?: string;
  disabled?: boolean;
  allowMalaysia?: boolean;
};

const FIELD_CLASS =
  "h-14 w-full rounded-2xl border border-input bg-card px-4 text-sm font-medium text-muted-foreground placeholder:text-border";

export default function NationalityField({
  value,
  onSelect,
  error,
  disabled = false,
  allowMalaysia = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<NationalityOption[]>([]);

  useEffect(() => {
    const fetchNationality = async () => {
      try {
        setLoading(true);

        const response = await GetNationality();
        const nationalityList =
          response.data?.countryList?.map((country) => ({
            value: country.code || country.name,
            label: country.name,
          })) ?? [];

        setOptions(
          nationalityList.filter(
            (country) =>
              allowMalaysia
                ? true
                : country.value !== "MYS" && country.label !== "Malaysia",
          ),
        );
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to get nationality options.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchNationality();
  }, [allowMalaysia]);

  useEffect(() => {
    const normalizedValue = normalizeNationalityValue(value, options);

    if (!allowMalaysia && (normalizedValue === "MYS" || value === "Malaysia")) {
      onSelect("");
    }
  }, [allowMalaysia, value, onSelect, options]);

  const normalizedValue = normalizeNationalityValue(value, options);
  const selectedOption = options.find((option) => option.value === normalizedValue);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold tracking-wide text-field">
        Nationality <span className="text-destructive">*</span>
      </Label>

      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger
          type="button"
          disabled={loading || disabled}
          className={cn(
            FIELD_CLASS,
            "flex items-center justify-between gap-2",
            "outline-none transition-colors",
            "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
            error && "border-destructive ring-3 ring-destructive/20",
            disabled && "cursor-not-allowed opacity-80",
          )}
        >
          <span className={cn("truncate", !selectedOption && "text-field")}>
            {loading
              ? "Loading nationality..."
              : (selectedOption?.label ?? "Select Nationality")}
          </span>

          {!disabled && <ChevronDown size={16} className="shrink-0 text-muted-foreground" />}
        </PopoverTrigger>

        {!disabled && (
          <PopoverContent
            className="w-(--radix-popover-trigger-width) p-0"
            align="start"
          >
            <Command>
              <CommandInput placeholder="Search nationality..." />

              <CommandList className="max-h-72">
                <CommandEmpty>No nationality found.</CommandEmpty>

                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => {
                        if (
                          !allowMalaysia &&
                          (option.value === "MYS" || option.label === "Malaysia")
                        ) {
                          return;
                        }

                        onSelect(option.value);
                        setOpen(false);
                      }}
                      className="flex items-center justify-between"
                    >
                      <span>{option.label}</span>

                      {normalizedValue === option.value && <Check className="h-4 w-4" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
